/**
 * Notification Sound System
 * Provides real-time sound effects for notifications
 * Works on both web and native platforms
 */

import { Capacitor } from '@capacitor/core';

// Sound cache to avoid reloading
const soundCache: Map<string, HTMLAudioElement> = new Map();

// Default notification sounds (using Web Audio API for cross-platform compatibility)
const NOTIFICATION_SOUNDS = {
  default: '/sounds/notification-default.mp3',
  message: '/sounds/notification-message.mp3',
  success: '/sounds/notification-success.mp3',
  error: '/sounds/notification-error.ogg',
  booking: '/sounds/notification-booking.mp3',
  payment: '/sounds/notification-payment.mp3',
};

/**
 * Play a notification sound
 * @param type - Type of sound to play
 * @param enabled - Whether sound is enabled in preferences
 */
export async function playNotificationSound(
  type: keyof typeof NOTIFICATION_SOUNDS = 'default',
  enabled: boolean = true
): Promise<void> {
  if (!enabled) return;

  // Check if we're in a browser environment
  if (typeof window === 'undefined') return;

  try {
    // Try to use cached audio element
    let audio = soundCache.get(type);
    
    if (!audio) {
      audio = new Audio(NOTIFICATION_SOUNDS[type] || NOTIFICATION_SOUNDS.default);
      audio.volume = 0.5; // 50% volume
      soundCache.set(type, audio);
    }

    // Reset to start and play
    audio.currentTime = 0;
    
    // Handle mobile autoplay restrictions
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.log('[NotificationSound] Audio play failed (likely autoplay restriction):', error);
        // Don't throw - just log the error
      });
    }
  } catch (error) {
    console.error('[NotificationSound] Error playing sound:', error);
  }
}

/**
 * Play sound using Web Audio API (more reliable on mobile)
 * @param frequency - Base frequency for the sound
 * @param type - Type of notification sound
 */
export function playSyntheticNotificationSound(
  type: 'default' | 'message' | 'success' | 'error' = 'default',
  enabled: boolean = true
): void {
  if (!enabled || typeof window === 'undefined') return;

  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const now = ctx.currentTime;

    // Different sound patterns for different types
    const soundConfigs = {
      default: { freq: 880, duration: 0.1, pattern: [0, 0.12] },
      message: { freq: 1200, duration: 0.08, pattern: [0, 0.08, 0.16] },
      success: { freq: 523.25, duration: 0.12, pattern: [0, 0.12, 0.24] },
      error: { freq: 200, duration: 0.15, pattern: [0, 0.1] },
    };

    const config = soundConfigs[type];

    config.pattern.forEach((delay, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = type === 'message' ? 'sine' : 'triangle';
      osc.frequency.setValueAtTime(
        config.freq + (i * 100),
        now + delay
      );

      gain.gain.setValueAtTime(0.1, now + delay);
      gain.gain.exponentialRampToValueAtTime(0.001, now + delay + config.duration);

      osc.connect(gain).connect(ctx.destination);
      osc.start(now + delay);
      osc.stop(now + delay + config.duration);
    });

    // Close audio context after playing
    setTimeout(() => {
      if (ctx.state !== 'closed') {
        ctx.close().catch(() => {});
      }
    }, 1000);

  } catch (error) {
    console.error('[NotificationSound] Web Audio API error:', error);
  }
}

/**
 * Preload notification sounds for faster playback
 */
export function preloadNotificationSounds(): void {
  if (typeof window === 'undefined') return;

  Object.entries(NOTIFICATION_SOUNDS).forEach(([type, src]) => {
    const audio = new Audio(src);
    audio.preload = 'auto';
    audio.volume = 0.5;
    soundCache.set(type, audio);
  });

  console.log('[NotificationSound] Sounds preloaded');
}

/**
 * Check if audio is supported
 */
export function isAudioSupported(): boolean {
  if (typeof window === 'undefined') return false;
  return !!(window.AudioContext || (window as any).webkitAudioContext || document.createElement('audio').canPlayType);
}

/**
 * Get the appropriate sound for a notification type
 */
export function getSoundForNotificationType(type: string): keyof typeof NOTIFICATION_SOUNDS {
  switch (type) {
    case 'new_message':
    case 'chat':
      return 'message';
    case 'booking_confirmed':
    case 'payment_confirmed':
    case 'payment_received':
      return 'success';
    case 'booking_created':
    case 'offer_received':
      return 'booking';
    case 'booking_cancelled':
    case 'error':
      return 'error';
    default:
      return 'default';
  }
}

